import * as React from 'react';
import { useCallback, useRef, useState } from 'react';

/** Material UI Packages. */
import { Box, Chip, Link, Typography } from '@mui/material';

/** Dayjs Packages. */
import dayjs from 'dayjs';

/** Internal Components. */
import DateWindowFields from 'shared/ui/DateWindowFields';
import FilterButtonPopover from 'shared/ui/FilterButtonPopover';
import { ChipOptionList, CheckboxOptionList, SearchableOptionList } from 'shared/ui/FilterOptionLists';
import { readSocketData } from 'shared/lib/fetchSocket';
import { formatDateToDateString } from 'shared/lib/dateFormatter';
import { dateWindowLimitText } from 'shared/lib/dateWindow';
import { formatDateWindowText, formatMultiValueText, optionText } from 'shared/lib/filterLabel';

import DeliveryLocationPicker from './DeliveryLocationPicker';
import { normalizeActiveTeamOptions } from './RateManagementAutocompletes';
import {
    RATE_SEARCH_DATE_FIELDS,
    RATE_SEARCH_DATE_WINDOW_MAX_DAYS,
    defaultRateSearchDateWindow,
    rateSearchDateError,
} from '../lib/rateManagementSearchCriteria';

/** Ports and airports are separate location lists: POL searches ports, AOL searches airports. */
const LOCATION_TAB = { OCEAN: 0, AIR: 2 };

/** The search-period date as the operator reads it ("etd" → "ETD"). */
export const dateFieldLabel = (field) => RATE_SEARCH_DATE_FIELDS.find((f) => f.value === field)?.label ?? 'ETD';

/**
 * Options found by what the operator types. When they type fast only the latest answer fills
 * the list, and the spinner stops whatever the outcome, so a failed search can be retried.
 */
export function useSearchedOptions(fetch) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const seqRef = useRef(0);
    const search = useCallback(
        (term) => {
            const seq = ++seqRef.current;
            setLoading(true);
            fetch(
                term,
                (received) => {
                    if (seq !== seqRef.current) return;
                    setOptions(Array.isArray(received) ? received : []);
                },
                () => {
                    if (seq === seqRef.current) setLoading(false);
                }
            );
        },
        [fetch]
    );
    return { options, loading, search };
}

/** Client, Shipper or Consignee picker. Client can take several (`multiple`), the others one. */
function TradePartnerFilter({
    label,
    fieldKey,
    value,
    onApply,
    dispatch,
    invalid = false,
    multiple = false,
    notice = null,
    blocked = null,
}) {
    const fetch = useCallback(
        (term, onSuccess, onFinally) =>
            readSocketData(dispatch, null, 'cell', 'il_trade_partner', fieldKey, term || '', onSuccess, null, onFinally),
        [dispatch, fieldKey]
    );
    const { options, loading, search } = useSearchedOptions(fetch);
    return (
        <FilterButtonPopover
            label={label}
            value={value}
            valueText={multiple ? formatMultiValueText(value) : optionText(value)}
            onApply={onApply}
            resetValue={multiple ? [] : null}
            invalid={invalid}
            notice={notice}
            blocked={blocked}
        >
            {(draft, setDraft) => (
                <SearchableOptionList
                    options={options}
                    value={draft}
                    onChange={setDraft}
                    onSearch={search}
                    loading={loading}
                    placeholder={`Search ${label.toLowerCase()}`}
                    multiple={multiple}
                />
            )}
        </FilterButtonPopover>
    );
}

const inlineLinkSx = { fontSize: 11, verticalAlign: 'baseline', cursor: 'pointer' };

/** Tells the operator in the Client popover why only one client can be picked, with a link to lift it. */
function ClientModeNotice({ shipper, consignee, onClearParty }) {
    const party = shipper?.id != null ? ['shipper', shipper] : consignee?.id != null ? ['consignee', consignee] : null;
    if (!party) return <span>Select one or several clients — no shipper or consignee is set.</span>;
    const [key, option] = party;
    return (
        <span>
            Single client only: {key} "{optionText(option)}" is set.{' '}
            <Link component="button" type="button" underline="hover" sx={inlineLinkSx} onClick={() => onClearParty(key)}>
                Clear {key}
            </Link>{' '}
            to pick several clients.
        </span>
    );
}

/**
 * Shipper/Consignee belong to one client's HBLs, so with several clients they cannot be used;
 * the popover says so and lets the operator drop clients down to one.
 */
function HousePartyBlocked({ clients, onRemoveClient }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#003a52' }}>
                Several clients are selected. Keep one of these to use this filter:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {clients.map((c) => (
                    <Chip
                        key={c.id}
                        size="small"
                        label={optionText(c)}
                        onDelete={() => onRemoveClient(c)}
                        sx={{ fontSize: '12px', height: 24 }}
                    />
                ))}
            </Box>
        </Box>
    );
}

function LocationFilter({ label, tab, value, onApply, dispatch }) {
    const fetch = useCallback(
        (term, onSuccess, onFinally) =>
            readSocketData(dispatch, tab, 'cell', 'il_location', '', term || '', onSuccess, null, onFinally),
        [dispatch, tab]
    );
    const { options, loading, search } = useSearchedOptions(fetch);
    return (
        <FilterButtonPopover label={label} value={value} valueText={optionText(value)} onApply={onApply}>
            {(draft, setDraft) => (
                <SearchableOptionList
                    options={options}
                    value={draft}
                    onChange={setDraft}
                    onSearch={search}
                    loading={loading}
                    placeholder="Search code or name"
                />
            )}
        </FilterButtonPopover>
    );
}

function TeamFilter({ value, onApply, dispatch, invalid = false }) {
    const [options, setOptions] = useState(null);
    // The first click fires both mousedown and focus; read the team list once.
    const requested = useRef(false);
    const load = () => {
        if (options !== null || requested.current) return;
        requested.current = true;
        readSocketData(
            dispatch,
            null,
            'row',
            'il_team',
            null,
            null,
            (received) => setOptions(normalizeActiveTeamOptions(received)),
            null,
            () => {
                requested.current = false;
            }
        );
    };
    return (
        <Box onMouseDown={load} onFocus={load}>
            <FilterButtonPopover label="Team" value={value} valueText={optionText(value)} onApply={onApply} invalid={invalid}>
                {(draft, setDraft) => <ChipOptionList options={options ?? []} value={draft} onChange={setDraft} />}
            </FilterButtonPopover>
        </Box>
    );
}

/**
 * Rate Type can take several: a shipment matches when it has a rate of any picked type.
 * If the list fails to load, opening the filter again retries.
 */
function RateTypeFilter({ value, onApply, dispatch }) {
    const [options, setOptions] = useState(null);
    const [loading, setLoading] = useState(false);
    const load = () => {
        if (options !== null || loading) return;
        setLoading(true);
        readSocketData(
            dispatch,
            0,
            'cell',
            'il_rate_type',
            '',
            '',
            (received) => setOptions(Array.isArray(received) ? received : []),
            null,
            () => setLoading(false)
        );
    };
    return (
        <Box onMouseDown={load} onFocus={load}>
            <FilterButtonPopover
                label="Rate Type"
                value={value}
                valueText={formatMultiValueText(value)}
                onApply={onApply}
                resetValue={[]}
            >
                {(draft, setDraft) => (
                    <CheckboxOptionList options={options ?? []} value={draft} onChange={setDraft} loading={loading} multiple />
                )}
            </FilterButtonPopover>
        </Box>
    );
}

/** What the operator sees while a picked address is being looked up, or when the lookup fails. */
const DELIVERY_LOOKUP_ERRORS = {
    pending: 'Looking up the address…',
    failed: 'Could not look up this address. Please pick it again.',
};

/**
 * Delivery address filter. A picked place is looked up with Google before it is set, so APPLY
 * waits until the lookup is done — otherwise the place just picked would be lost without notice.
 */
function DeliveryLocationFilter({ value, onApply }) {
    const [lookup, setLookup] = useState(null);
    return (
        <FilterButtonPopover
            label="Delivery Location"
            value={value || null}
            valueText={value}
            onApply={(v) => onApply(v || '')}
            resetValue=""
            error={DELIVERY_LOOKUP_ERRORS[lookup] ?? null}
        >
            {(draft, setDraft) => (
                <DeliveryLocationPicker value={draft ?? ''} onChange={setDraft} onLookupChange={setLookup} />
            )}
        </FilterButtonPopover>
    );
}

/**
 * Search period: only shipments whose ETD (or ETA) falls in it are shown, closed or not.
 * A period always applies — RESET goes back to the past year up to today. Up to two years.
 */
function DateWindowFilter({ value, onApply }) {
    const toPicker = ({ start, end }) => [start ? dayjs(start) : null, end ? dayjs(end) : null];
    const fromPicker = (field, [start, end]) => ({
        field,
        start: start?.isValid() ? formatDateToDateString(start.toDate()) : null,
        end: end?.isValid() ? formatDateToDateString(end.toDate()) : null,
    });
    return (
        <FilterButtonPopover
            label={dateFieldLabel(value.field)}
            title={null}
            value={value}
            valueText={formatDateWindowText(value.start, value.end)}
            onApply={onApply}
            resetValue={defaultRateSearchDateWindow()}
            width={280}
            error={rateSearchDateError}
        >
            {(draft, setDraft) => (
                <DateWindowBody draft={draft} setDraft={setDraft} toPicker={toPicker} fromPicker={fromPicker} />
            )}
        </FilterButtonPopover>
    );
}

/** Period picker in the popover; an invalid period turns the fields red and the popover shows why. */
function DateWindowBody({ draft, setDraft, toPicker, fromPicker }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DateWindowFields
                fields={RATE_SEARCH_DATE_FIELDS}
                field={draft.field}
                onFieldChange={(field) => setDraft({ ...draft, field })}
                range={toPicker(draft)}
                onRangeChange={(range) => setDraft(fromPicker(draft.field, range))}
                error={Boolean(rateSearchDateError(draft))}
                title={`Date range (up to ${dateWindowLimitText(RATE_SEARCH_DATE_WINDOW_MAX_DAYS)})`}
            />
        </Box>
    );
}

/**
 * Rate Management search conditions, one button each, the search period first. Picks take
 * effect in the bar on APPLY; results change only when SEARCH is pressed. Several clients and
 * a Shipper/Consignee cannot be used together, and each popover says why.
 *
 * @param {object} filters - { dateWindow, clients (array), team, shipper, consignee, pol, aol, fpod, deliveryLocation, rateTypes (array) }
 * @param {function} onChange - Called with (key, value) when a popover applies a value.
 * @param {boolean} requiredMissing - The page rejected a search because neither Client nor Team is set; both get a red border.
 */
export default function RateManagementFilterBar({ filters, onChange, dispatch, requiredMissing = false }) {
    const set = (key) => (value) => onChange(key, value);
    const clients = Array.isArray(filters.clients) ? filters.clients : [];
    const hasHouseParty = filters.shipper?.id != null || filters.consignee?.id != null;
    const clientMultiple = !hasHouseParty;
    const manyClients = clients.length > 1;
    const asClientList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    const removeClient = (c) => set('clients')(clients.filter((x) => String(x.id) !== String(c.id)));
    const houseParty = manyClients ? <HousePartyBlocked clients={clients} onRemoveClient={removeClient} /> : null;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <DateWindowFilter value={filters.dateWindow} onApply={set('dateWindow')} />
            <TeamFilter value={filters.team} onApply={set('team')} dispatch={dispatch} invalid={requiredMissing} />
            <TradePartnerFilter
                label="Client"
                fieldKey="il_trade_partner_id_client_pricing"
                value={clientMultiple ? clients : (clients[0] ?? null)}
                onApply={(v) => set('clients')(asClientList(v))}
                dispatch={dispatch}
                invalid={requiredMissing}
                multiple={clientMultiple}
                notice={
                    <ClientModeNotice
                        shipper={filters.shipper}
                        consignee={filters.consignee}
                        onClearParty={(key) => set(key)(null)}
                    />
                }
            />
            <TradePartnerFilter
                label="Shipper"
                fieldKey="il_trade_partner_id_shipper"
                value={filters.shipper}
                onApply={set('shipper')}
                dispatch={dispatch}
                blocked={houseParty}
            />
            <TradePartnerFilter
                label="Consignee"
                fieldKey="il_trade_partner_id_consignee"
                value={filters.consignee}
                onApply={set('consignee')}
                dispatch={dispatch}
                blocked={houseParty}
            />
            <LocationFilter label="POL" tab={LOCATION_TAB.OCEAN} value={filters.pol} onApply={set('pol')} dispatch={dispatch} />
            <LocationFilter label="AOL" tab={LOCATION_TAB.AIR} value={filters.aol} onApply={set('aol')} dispatch={dispatch} />
            <LocationFilter
                label="F.DEST"
                tab={filters.aol?.id != null ? LOCATION_TAB.AIR : LOCATION_TAB.OCEAN}
                value={filters.fpod}
                onApply={set('fpod')}
                dispatch={dispatch}
            />
            <DeliveryLocationFilter value={filters.deliveryLocation} onApply={set('deliveryLocation')} />
            <RateTypeFilter value={filters.rateTypes} onApply={set('rateTypes')} dispatch={dispatch} />
        </Box>
    );
}
